import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaSearch, FaMapMarkerAlt, FaBriefcase,
    FaArrowRight, FaRegCalendarAlt, FaBuilding, FaCheckCircle,
    FaTimesCircle, FaCalendarCheck, FaHourglassHalf, FaBan, FaEye,
    FaClock, FaExclamationTriangle
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading, showConfirm } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import '../../Student/views/StudentJobPortal.css';
import PublicJobDetail from '../../PublicPortal/PublicJobDetail';
import { API_URL } from '../../../config';

/* Colour palette for company logos – deterministic based on first char */
const LOGO_COLORS = [
    ['#1E3A8A', '#3B82F6'],
    ['#065F46', '#10B981'],
    ['#7C2D12', '#F97316'],
    ['#4C1D95', '#8B5CF6'],
    ['#831843', '#EC4899'],
    ['#1E3A8A', '#6366F1'],
    ['#134E4A', '#14B8A6'],
    ['#713F12', '#EAB308'],
    ['#0C4A6E', '#0EA5E9'],
    ['#3F1F1F', '#EF4444'],
];

const getLogoColor = (name = '') => {
    const c = name.charCodeAt(0) || 65;
    return LOGO_COLORS[c % LOGO_COLORS.length];
};

const STATUS_CONFIG = {
    Pending: { label: 'Applied', color: '#92400E', bg: '#FEF3C7', icon: <FaHourglassHalf /> },
    Shortlisted: { label: 'Shortlisted ✓', color: '#065F46', bg: '#D1FAE5', icon: <FaCheckCircle /> },
    Rejected: { label: 'Not Selected', color: '#B91C1C', bg: '#FEE2E2', icon: <FaTimesCircle /> },
    'Interview Scheduled': { label: 'Interview Scheduled', color: '#1D4ED8', bg: '#DBEAFE', icon: <FaCalendarCheck /> },
    'Interview Completed': { label: 'Interview Completed', color: '#065F46', bg: '#D1FAE5', icon: <FaCheckCircle /> },
    Withdrawn: { label: 'Withdrawn', color: '#475569', bg: '#F1F5F9', icon: <FaBan /> }
};

/**
 * Checks if the user's career profile has the minimum required fields filled.
 * Required: at least 1 skill, a bio, and a base location.
 */
const isProfileComplete = (profile) => {
    if (!profile) return false;
    const hasSkills = (profile.academicInfo?.skills || []).length > 0;
    const hasBio = !!profile.bio?.trim();
    const hasLocation = !!profile.location?.trim();
    return hasSkills && hasBio && hasLocation;
};

const JobBoard = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState({});   // jobId → status
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [selectedJob, setSelectedJob] = useState(null);  // card-click detail modal
    const [userProfile, setUserProfile] = useState(null);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [jobsRes, appsRes, profileRes] = await Promise.all([
                axios.get('/api/student/jobs', { headers }),
                axios.get('/api/student/applications', { headers }),
                axios.get('/api/users/me', { headers })
            ]);
            setJobs(jobsRes.data);
            setUserProfile(profileRes.data);

            const appMap = {};
            appsRes.data.forEach(app => {
                const id = app.jobId?._id || app.jobId;
                if (id) appMap[id] = app.status;
            });
            setApplications(appMap);
            setLoading(false);
        } catch (err) {
            showError('Error', 'Failed to fetch job postings');
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleApply = async (job) => {
        // ── Profile Completion Gate ──────────────────────────────────────
        if (!isProfileComplete(userProfile)) {
            const { default: Swal } = await import('sweetalert2');
            await Swal.fire({
                icon: 'warning',
                title: 'Complete Your Profile First',
                html: `
                    <p style="font-size:15px;color:#475569;line-height:1.7;margin:0 0 16px">
                        Your Career Profile is incomplete. Recruiters need to see your skills,
                        bio, and location before you can apply.
                    </p>
                    <div style="background:#FEF3C7;border-left:4px solid #D97706;padding:12px 16px;border-radius:8px;text-align:left;font-size:13px;color:#92400E;">
                        <strong>Missing fields:</strong><br/>${[
                        !(userProfile?.academicInfo?.skills?.length) && 'Technical Skills',
                        !userProfile?.bio?.trim() && 'Bio / Executive Summary',
                        !userProfile?.location?.trim() && 'Base Location'
                    ].filter(Boolean).join('<br/>')}
                    </div>
                `,
                confirmButtonText: 'Complete My Profile',
                confirmButtonColor: '#002C5F',
                showCancelButton: true,
                cancelButtonText: 'Not Now',
                cancelButtonColor: '#94a3b8'
            });
            navigate('/jobseeker-dashboard/profile');
            return;
        }
        // ────────────────────────────────────────────────────────────────

        const confirmResult = await showConfirm(
            'Apply for Position',
            `Confirm application for "${job.title}" at ${job.company}? Your verified resume will be shared with the recruiter.`
        );
        if (!confirmResult.isConfirmed) return;

        showLoading('Submitting Application…');
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/student/jobs/${job._id}/apply`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess('Submitted!', 'Your application has been received. You will be notified of any updates.');
            setApplications(prev => ({ ...prev, [job._id]: 'Pending' }));
            setSelectedJob(null);
        } catch (err) {
            closeLoading();
            showError('Application Failed', err.response?.data?.message || 'Could not submit your application.');
        }
    };

    const filteredJobs = jobs.filter(j =>
        (j.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            j.company?.toLowerCase().includes(searchTerm.toLowerCase())) &&
        j.location?.toLowerCase().includes(locationFilter.toLowerCase())
    );

    const profileComplete = isProfileComplete(userProfile);

    if (loading) return <Loader />;

    return (
        <div className="job-portal fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Career Gateway</h1>
                <p className="page-subtitle">Verified opportunities from our enterprise partners, tailored to your career profile.</p>
            </div>

            {/* Profile incomplete banner */}
            {!profileComplete && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px 24px',
                    background: '#FFFBEB',
                    border: '1px solid #FDE68A',
                    borderLeft: '5px solid #D97706',
                    borderRadius: '14px',
                    marginBottom: '8px'
                }}>
                    <FaExclamationTriangle style={{ color: '#D97706', fontSize: '22px', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: 800, fontSize: '14px', color: '#92400E' }}>
                            Career Profile Incomplete — Applications Locked
                        </p>
                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#B45309' }}>
                            Fill in your skills, bio, and location to unlock the Apply Now button.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/jobseeker-dashboard/profile')}
                        style={{
                            padding: '9px 20px',
                            background: '#D97706',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '10px',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            flexShrink: 0
                        }}
                    >
                        Complete Profile →
                    </button>
                </div>
            )}

            {/* Stats strip */}
            <div className="sjp-stats-strip">
                <div className="sjp-stat">
                    <span>{jobs.length}</span>
                    <p>Open Roles</p>
                </div>
                <div className="sjp-stat">
                    <span>{Object.keys(applications).length}</span>
                    <p>Applied</p>
                </div>
                <div className="sjp-stat sjp-stat-highlight">
                    <span>{Object.values(applications).filter(s => s === 'Interview Scheduled').length}</span>
                    <p>Interviews</p>
                </div>
            </div>

            {/* Search */}
            <div className="job-search-strip">
                <div style={{ flex: 1, position: 'relative' }}>
                    <FaSearch className="sjp-search-icon" />
                    <input
                        className="glass-input sjp-search-input"
                        placeholder="Search by title, company or skill…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div style={{ flex: 0.5, position: 'relative' }}>
                    <FaMapMarkerAlt className="sjp-search-icon" />
                    <input
                        className="glass-input sjp-search-input"
                        placeholder="Location…"
                        value={locationFilter}
                        onChange={e => setLocationFilter(e.target.value)}
                    />
                </div>
            </div>

            {/* Job list */}
            <div className="job-list">
                {filteredJobs.length > 0 ? filteredJobs.map(job => {
                    const appStatus = applications[job._id];
                    const statusCfg = STATUS_CONFIG[appStatus];
                    const [from, to] = getLogoColor(job.company);
                    const salary = job.salaryRange || job.salary || null;

                    return (
                        <div
                            key={job._id}
                            className={`job-card ${appStatus ? 'sjp-applied' : ''}`}
                            onClick={() => setSelectedJob(job)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="job-main-info">
                                {/* Coloured company logo */}
                                <div
                                    className="company-logo"
                                    style={{ background: job.companyId?.logoUrl ? 'white' : `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}
                                >
                                    {job.companyId?.logoUrl ? (
                                        <img
                                            src={`${API_URL}${job.companyId.logoUrl}`}
                                            alt={job.company}
                                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                                        />
                                    ) : (
                                        <span className="company-logo-letter">{job.company?.charAt(0)?.toUpperCase()}</span>
                                    )}
                                </div>

                                <div className="job-details">
                                    <h3 className="job-title-text">{job.title}</h3>
                                    <div className="company-name">{job.company}</div>
                                    <div className="job-tags">
                                        <span className="job-tag job-tag-location">
                                            <FaMapMarkerAlt /> {job.location}
                                        </span>
                                        {job.experience && (
                                            <span className="job-tag job-tag-exp">
                                                <FaBriefcase /> {job.experience}
                                            </span>
                                        )}
                                        <span className="job-tag job-tag-date">
                                            <FaRegCalendarAlt /> {new Date(job.postedAt || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </span>
                                        {job.type && (
                                            <span className="job-tag job-tag-type">
                                                <FaClock /> {job.type}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="job-action-area" onClick={e => e.stopPropagation()}>
                                {salary ? (
                                    <div className="salary-text">
                                        Pay: {salary}
                                    </div>
                                ) : (
                                    <div className="salary-text salary-negotiable">Open to Negotiate</div>
                                )}

                                {appStatus ? (
                                    <div className="sjp-status-area">
                                        <span className="sjp-status-badge" style={{ background: statusCfg.bg, color: statusCfg.color }}>
                                            {statusCfg.icon} {statusCfg.label}
                                        </span>
                                        <button className="sjp-view-btn" onClick={(e) => { e.stopPropagation(); setSelectedJob(job); }}>
                                            <FaEye /> View Details
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        className="apply-btn"
                                        onClick={(e) => { e.stopPropagation(); handleApply(job); }}
                                        title={!profileComplete ? 'Complete your profile first' : ''}
                                    >
                                        Apply Now <FaArrowRight />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                }) : (
                    <div className="sjp-empty-state">
                        <FaBuilding size={48} />
                        <h3>No opportunities found</h3>
                        <p>Try broadening your search or location filter.</p>
                    </div>
                )}
            </div>

            {/* ──────────────────────────────────────────────────────────────────
                Job Detail Modal — Portaled to document.body 
                (so it sits above sidebar + content, both of which appear blurred)
            ────────────────────────────────────────────────────────────────── */}
            {selectedJob && (
                <PublicJobDetail
                    jobId={selectedJob._id}
                    onClose={() => setSelectedJob(null)}
                    onApply={() => handleApply(selectedJob)}
                    appStatus={applications[selectedJob._id]}
                />
            )}
        </div>
    );
};

export default JobBoard;
